/**
 * Scenario Utility Functions
 * 시나리오 관련 유틸리티 함수
 */

import type {
	GetScenarioListByUserIdDto,
	GetScenarioBasicInfoResponseDto,
	UpdateScenarioBasicInfoRequestDto,
	ScenarioAdminDto,
} from '@/types/api/scenario';
import { normalizeLocaleTag } from '@/lib/i18nLocale';
import { formatLocaleDateShort } from '@/lib/localeFormat';
import { Scenario } from '@/types/admin';

/**
 * GetScenarioListByUserIdDto를 Scenario 타입으로 변환
 */
/**
 * 시나리오 목록 등록일 표시용: UI 언어별 표준 날짜
 * - ISO datetime, YYYY-MM-DD, ko-KR 점 구분(2026. 04. 07.) 등 수용
 */
export function formatScenarioListDate(input: string | undefined | null): string {
	if (input == null || typeof input !== 'string') return '';
	const trimmed = input.trim();
	if (!trimmed) return '';

	const isoPrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
	if (isoPrefix) return formatLocaleDateShort(isoPrefix[1]);

	if (trimmed.includes('T')) {
		const part = trimmed.split('T')[0];
		if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return formatLocaleDateShort(part);
	}

	const dotted = trimmed.match(/^(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
	if (dotted) {
		const y = dotted[1];
		const m = dotted[2].padStart(2, '0');
		const day = dotted[3].padStart(2, '0');
		return formatLocaleDateShort(`${y}-${m}-${day}`);
	}

	const t = Date.parse(trimmed);
	if (!Number.isNaN(t)) {
		return formatLocaleDateShort(new Date(t).toISOString());
	}

	return '';
}

/** 시나리오 관리 목록 — 카테고리 API 응답 정규화 */
export interface ScenarioListCategoryRow {
	id: number;
	title: string;
}

export function extractScenarioListCategoryRows(raw: unknown): ScenarioListCategoryRow[] {
	const list =
		(raw as { scenarioCategoryList?: unknown[] })?.scenarioCategoryList ??
		(raw as { data?: { scenarioCategoryList?: unknown[] } })?.data?.scenarioCategoryList ??
		[];
	return (list as { scenarioCategoryId?: number; id?: number; title?: string }[])
		.map((c) => ({
			id: Number(c.scenarioCategoryId ?? c.id),
			title: (c.title ?? '').trim(),
		}))
		.filter((c) => !Number.isNaN(c.id) && c.title.length > 0 && c.id > 0);
}

/** 시나리오 관리 목록 — 디바이스 타입 API 응답 정규화 (등록 화면과 동일 소스) */
export interface ScenarioListDeviceTypeRow {
	id: number;
	title: string;
}

export function extractScenarioListDeviceTypeRows(raw: unknown): ScenarioListDeviceTypeRow[] {
	const list =
		(raw as { deviceTypeList?: unknown[] })?.deviceTypeList ??
		(raw as { data?: { deviceTypeList?: unknown[] } })?.data?.deviceTypeList ??
		[];
	return (list as { deviceTypeId?: number; id?: number; title?: string }[])
		.map((d) => ({
			id: Number(d.deviceTypeId ?? d.id),
			title: String(d.title ?? ''),
		}))
		.filter((d) => !Number.isNaN(d.id));
}

/** AdminScenarioAdd 의 VR / Mobile / PC 선택과 동일한 title 매칭 → deviceTypeId */
export type ScenarioListPlatformFilterKey = 'VR' | 'MOBILE' | 'PC';

/** GET /class/open/list?scenarioDeviceTypeId=… — 기기 API가 비었을 때만 사용(백엔드 ID: VR=1, MOBILE=3, PC=4) */
export const OPEN_LIST_FALLBACK_DEVICE_TYPE_IDS: Record<ScenarioListPlatformFilterKey, number> = {
	VR: 1,
	MOBILE: 3,
	PC: 4,
};

export function resolveDeviceTypeIdForScenarioListPlatform(
	devices: ScenarioListDeviceTypeRow[],
	platform: ScenarioListPlatformFilterKey
): number | undefined {
	const exact = devices.find((d) => {
		const s = d.title.trim();
		const sl = s.toLowerCase();
		if (platform === 'VR') return /^vr$/i.test(s);
		if (platform === 'MOBILE') return /^(mobile|모바일)$/i.test(s) || sl === 'mobile';
		if (platform === 'PC') return /^(pc|데스크톱|desktop)$/i.test(s) || sl === 'pc';
		return false;
	});
	if (exact) return exact.id;

	const row = devices.find((d) => {
		const s = d.title.toLowerCase();
		if (platform === 'VR') return /vr/i.test(s);
		if (platform === 'MOBILE') return /mobile|모바일|android|ios/i.test(s);
		if (platform === 'PC') return /pc|desktop|웹|web|windows|데스크톱/i.test(s);
		return false;
	});
	return row?.id;
}

/**
 * 오픈클래스 목록 GET /class/open/list 의 scenarioDeviceTypeId 용.
 * 제목 매칭 실패·기기 API 공백 시에도 폴백 ID로 요청해 플랫폼 버튼이 무반응이 되지 않게 함(백엔드 ID 정책과 맞출 것).
 */
export function resolveDeviceTypeIdForOpenClassListPlatform(
	devices: ScenarioListDeviceTypeRow[],
	platform: ScenarioListPlatformFilterKey | 'all'
): number | undefined {
	if (platform === 'all') return undefined;
	return (
		resolveDeviceTypeIdForScenarioListPlatform(devices, platform) ??
		OPEN_LIST_FALLBACK_DEVICE_TYPE_IDS[platform]
	);
}

/**
 * 예상 플레이 시간(초) → 한글 표기 (저장된 초 단위를 그대로 반영)
 * 예: 479 → "7분 59초", 60 → "1분", 45 → "45초"
 */
export function formatExpectedPlaySecondsForDisplay(totalSeconds: number): string {
	const secs = Math.max(0, Math.floor(Number(totalSeconds) || 0));
	if (secs === 0) return '0초';
	const h = Math.floor(secs / 3600);
	const m = Math.floor((secs % 3600) / 60);
	const s = secs % 60;
	const parts: string[] = [];
	if (h > 0) parts.push(`${h}시간`);
	if (m > 0) parts.push(`${m}분`);
	if (s > 0) parts.push(`${s}초`);
	return parts.join(' ');
}

/**
 * 메타정보·예상소요시간 입력 문자열 → 초 (API expectedPlayTimeSeconds)
 * 지원: "479", "479초", "7분 59초", "7분59초", "1시간 5분", "30분" 등
 */
export function parseEstimatedDurationToSeconds(text: string): number | null {
	const raw = text.trim();
	if (!raw) return null;

	const hasUnit = /(\d+)\s*(?:시간|분|초|secs?)/i.test(raw);
	if (hasUnit) {
		let total = 0;
		const hourM = raw.match(/(\d+)\s*시간/);
		const minM = raw.match(/(\d+)\s*분/);
		const secM = raw.match(/(\d+)\s*(?:초|secs?)/i);
		if (hourM) total += parseInt(hourM[1], 10) * 3600;
		if (minM) total += parseInt(minM[1], 10) * 60;
		if (secM) total += parseInt(secM[1], 10);
		if (total > 0 && total <= 86400) return total;
		return null;
	}

	if (/^\d+$/.test(raw)) {
		const n = parseInt(raw, 10);
		if (n > 0 && n <= 86400) return n;
		return null;
	}

	return null;
}

/** OpenAPI가 number 외 타입으로 잘못 표기한 필드 대비 */
function coerceNonNegativeSeconds(v: unknown): number {
	if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
	if (typeof v === 'string' && v.trim()) {
		const n = Number(v);
		if (Number.isFinite(n) && n >= 0) return n;
	}
	return 0;
}

function normalizeMapItemIdForUi(
	v: number | string | null | Record<string, unknown> | undefined
): { label: string; mapId?: string } {
	if (v == null) return { label: '—' };
	if (typeof v === 'number' && Number.isFinite(v)) {
		const s = String(Math.trunc(v));
		return { label: `맵 #${s}`, mapId: s };
	}
	if (typeof v === 'string') {
		const t = v.trim();
		if (!t) return { label: '—' };
		return { label: `맵 #${t}`, mapId: t };
	}
	if (typeof v === 'object') {
		const id = (v as { id?: unknown }).id ?? (v as { value?: unknown }).value;
		if (typeof id === 'number' && Number.isFinite(id)) {
			const s = String(Math.trunc(id));
			return { label: `맵 #${s}`, mapId: s };
		}
		if (typeof id === 'string' && id.trim()) {
			const s = id.trim();
			return { label: `맵 #${s}`, mapId: s };
		}
	}
	return { label: '—' };
}

function coerceUrlLikeString(v: unknown): string {
	if (v == null) return '';
	if (typeof v === 'string') return v;
	if (typeof v === 'number' && Number.isFinite(v)) return String(v);
	// object로 내려오는 경우: { value: "..." } 또는 { url: "..." } 등 모든 string 값 탐색
	if (typeof v === 'object') {
		const o = v as Record<string, unknown>;
		// 우선순위 높은 키 먼저
		for (const k of ['value', 'url', 'thumbnailImageUrl', 'imageUrl', 'src']) {
			if (typeof o[k] === 'string' && o[k]) return o[k] as string;
		}
		// 그 외 http로 시작하는 string 값
		for (const val of Object.values(o)) {
			if (typeof val === 'string' && val.startsWith('http')) return val;
		}
	}
	return '';
}

/**
 * 썸네일 등 미디어 URL — API가 `/path`만 주는 경우 REACT_APP_API_BASE_URL 앞에 붙임
 */
export function resolveScenarioMediaUrl(raw: string): string {
	const s = (raw ?? '').trim();
	if (!s) return '';
	if (/^https?:\/\//i.test(s)) return s;
	const base =
		typeof process !== 'undefined' && process.env.REACT_APP_API_BASE_URL != null
			? String(process.env.REACT_APP_API_BASE_URL).replace(/\/$/, '')
			: '';
	if (s.startsWith('/') && base) {
		return `${base}${s}`;
	}
	return s;
}


function pickFirstPositiveInt(...vals: unknown[]): number | undefined {
	for (const v of vals) {
		if (v == null) continue;
		const n = typeof v === 'number' ? v : Number(v);
		if (Number.isFinite(n) && n > 0) return Math.trunc(n);
	}
	return undefined;
}

/** 객체 안에서 *category* + *id* 패턴의 숫자 필드 탐색 */
function scanRecordForCategoryLikeId(o: Record<string, unknown>): number | undefined {
	for (const [k, v] of Object.entries(o)) {
		const kl = k.toLowerCase();
		if (!kl.includes('category')) continue;
		if (!kl.includes('id')) continue;
		const n = typeof v === 'number' ? v : Number(v);
		if (Number.isFinite(n) && n > 0) return Math.trunc(n);
	}
	return undefined;
}

/**
 * GET /scenario/{id}/info 응답에서 카테고리 ID 추출
 * OpenAPI 스펙: scenarioCategoryId (number) 직접 반환
 */
export function tryExtractScenarioCategoryIdFromBasicInfo(
	info: GetScenarioBasicInfoResponseDto
): number | undefined {
	if (info.scenarioCategoryId > 0) return info.scenarioCategoryId;
	// 런타임 안전망: 스펙 외 필드로 내려올 경우 대비
	const any = info as unknown as Record<string, unknown>;
	return pickFirstPositiveInt(any.scenarioCategoryId, any.scenario_category_id);
}

export type BuildUpdateScenarioBasicInfoFallbacks = {
	scenarioCategoryId?: number;
	modeValue?: string;
	deviceTypeIdList?: number[];
};

/**
 * GET /scenario/{id}/info 응답 + 화면에서 수정한 필드 → PATCH /scenario/{id}/info 본문
 * (다국어: scenarioTranslations / learningObjectiveTranslations — i18n-api-changes)
 */
export function buildUpdateScenarioBasicInfoPayload(
	info: GetScenarioBasicInfoResponseDto,
	overrides: { handoverNote: string; objectiveTitles: string[] },
	fallbacks: BuildUpdateScenarioBasicInfoFallbacks | undefined,
	language: string
): UpdateScenarioBasicInfoRequestDto {
	const fromInfo = tryExtractScenarioCategoryIdFromBasicInfo(info);
	const scenarioCategoryId = pickFirstPositiveInt(
		fromInfo,
		fallbacks?.scenarioCategoryId
	);
	if (scenarioCategoryId == null) {
		throw new Error(
			'시나리오 카테고리 ID를 확인할 수 없습니다. 다른 계정이 만든 시나리오이거나, 목록·기본정보·상세 API에 카테고리 ID가 내려오지 않는 경우입니다.'
		);
	}

	const trimmedTitles = overrides.objectiveTitles.map((s) => String(s).trim()).filter(Boolean);
	const fromServer = (info.learningObjectives ?? []).map((lo) => lo.title).filter(Boolean);
	const learningObjective =
		trimmedTitles.length > 0
			? trimmedTitles.join('\n')
			: fromServer.length > 0
				? fromServer.join('\n')
				: undefined;

	const fromDevices = (info.deviceTypeIdList ?? []).filter(
		(n) => typeof n === 'number' && Number.isFinite(n) && n >= 0
	);
	const deviceTypeIdList =
		fromDevices.length > 0 ? fromDevices : (fallbacks?.deviceTypeIdList ?? undefined);

	const thumb = coerceUrlLikeString(info.thumbnailImageUrl);
	const thumbnailImageUrl = thumb || 'https://i.namu.wiki/i/1WKJVdliAkPdhI7i3umKo_7-7i2EKIazzB5wiwecX_EiH_ZjWoZB5DA2mcpyxSLYkBC7DWorw8DEtb6lB-8kuSLrfRqbKyaqsuZbddwp74Z2aZwdLrX--AiQgtCPt2NnTXJ7JhKGnh0fguph27M4wA.webp';

	// modeValueList 우선, 없으면 구버전 modeValue 필드로 구성
	const apiModeList = info.modeValueList ?? [];
	let modeValueList: { modeValue: string; modeValueTitle?: string }[];
	if (apiModeList.length > 0) {
		modeValueList = apiModeList.map((m) => ({
			modeValue: String(m.modeValue ?? '').trim() || '1',
			...(m.modeValueTitle ? { modeValueTitle: String(m.modeValueTitle).trim() } : {}),
		}));
	} else {
		const modeValueStr = String(info.modeValue ?? fallbacks?.modeValue ?? '').trim();
		const modeValueTitle = typeof info.modeValueTitle === 'string' ? info.modeValueTitle.trim() : undefined;
		modeValueList = [{ modeValue: modeValueStr || '1', ...(modeValueTitle ? { modeValueTitle } : {}) }];
	}

	// 메타 필드: info 원본 값 그대로 유지 (null/object 방어 처리)
	const extractStr = (v: unknown): string | undefined => {
		if (v == null) return undefined;
		if (typeof v === 'string') return v.trim() || undefined;
		if (typeof v === 'object') {
			const o = v as Record<string, unknown>;
			for (const k of ['value', 'str', 'text', 'content', 'data']) {
				if (typeof o[k] === 'string' && (o[k] as string).trim()) return (o[k] as string).trim();
			}
		}
		return undefined;
	};
	const evaluationTypeRaw = extractStr(info.evaluationType);

	const modeTitle0 =
		modeValueList.length > 0 && modeValueList[0].modeValueTitle
			? String(modeValueList[0].modeValueTitle).trim()
			: undefined;

	const m0 = modeValueList[0] ?? { modeValue: '1' };
	return {
		key: String(info.key ?? ''),
		modeValue: m0.modeValue,
		/* mode 표시명은 scenarioTranslations에만 (PATCH DTO 루트 modeValueTitle 없음) */
		scenarioTranslations: [
			{
				language,
				title: String(info.title ?? ''),
				...(overrides.handoverNote?.trim() ? { handoverNote: overrides.handoverNote.trim() } : {}),
				...(modeTitle0 ? { modeValueTitle: modeTitle0 } : {}),
			},
		],
		learningObjectiveTranslations: learningObjective?.trim()
			? [{ language, title: learningObjective.trim() }]
			: [],
		thumbnailImageUrl,
		expectedPlayTimeSeconds:
			Math.max(0, coerceNonNegativeSeconds(info.expectedPlayTimeSeconds)) || 900,
		scenarioCategoryId,
		deviceTypeIdList,
		evaluationType: evaluationTypeRaw || undefined,
		deviceModel: extractStr(info.deviceModel),
	};
}

export const convertScenarioListDtoToScenario = (dto: GetScenarioListByUserIdDto): Scenario => {
	const anyDto = dto as GetScenarioListByUserIdDto &
		Record<string, unknown> & {
			created_at?: string;
			scenario_id?: number;
			scenario_category_list?: { title?: string }[];
			expected_play_time_seconds?: number;
			thumbnail_image_url?: string;
		};

	const scenarioId = Number(anyDto.scenarioId ?? anyDto.scenario_id ?? 0);
	const title = String(anyDto.title ?? '');
	const categoryList =
		(anyDto.scenarioCategoryList as { title?: string }[] | undefined) ??
		anyDto.scenario_category_list ??
		[];
	const version = String(anyDto.version ?? '');
	const expectedSeconds = Number(
		anyDto.expectedPlayTimeSeconds ?? anyDto.expected_play_time_seconds ?? 0
	);
	const thumb =
		String(anyDto.thumbnailImageUrl ?? anyDto.thumbnail_image_url ?? '') || undefined;

	const dateOnly = formatScenarioListDate(anyDto.createdAt ?? anyDto.created_at);
	const secVal = Number.isFinite(expectedSeconds) ? expectedSeconds : 0;
	const durationLabel = formatExpectedPlaySecondsForDisplay(secVal);

	// deviceNames: 스펙 외 필드이지만 런타임에 내려오면 사용, 없으면 '-'
	const deviceNamesRaw = (anyDto as Record<string, unknown>)['deviceNames'];
	const deviceNameList: string[] = Array.isArray(deviceNamesRaw)
		? deviceNamesRaw.map((x) => String(x)).filter(Boolean)
		: [];
	const platformStr = deviceNameList.length > 0 ? deviceNameList.join(', ') : '-';

	return {
		id: scenarioId,
		title,
		classId: '',
		category: categoryList.map((c) => c?.title ?? '').filter(Boolean).join(', '),
		platform: platformStr as any,
		status: 'active', // status 필드가 없으면 기본값
		createdDate: dateOnly,
		updatedDate: dateOnly,
		version,
		averageDuration: durationLabel,
		views: 0, // views 필드 없음
		userCount: 0, // userCount 필드 없음
		simulationCount: 0, // simulationCount 필드 없음
		ContributedBy: '',
		learningObjectives: [],
		duration: durationLabel,
		supportedDevices: [],
		videoUrl: '',
		thumbnail: thumb ?? '',
		handover: '',
		keyTasks: [],
		simulationExamples: [],
		isPublic: true,
		averageScore: 0,
	};
};


/**
 * `handoverNote`가 string 이거나, `{ ko, en, ar, value, ... }` 언어 맵/래핑이면
 * `locale`에 맞는 문자열만 취함 (기존: object면 JSON.stringify 해 한글이 가려짐)
 */
export function pickHandoverTextForLocale(raw: unknown, locale: string | undefined): string {
	if (raw == null) return '';
	if (typeof raw === 'string') return raw;
	if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
		const o = raw as Record<string, unknown>;
		const base = locale ? normalizeLocaleTag(locale) : 'en';
		if (base) {
			for (const k of [base, 'ko', 'en', 'ar', 'en-US', 'ko-KR'] as const) {
				const kk = k.split('-')[0] ?? k;
				if (k in o && typeof o[k] === 'string' && (o[k] as string).trim()) {
					return (o[k] as string).trim();
				}
				if (kk in o && typeof o[kk] === 'string' && (o[kk] as string).trim()) {
					return (o[kk] as string).trim();
				}
			}
		}
		for (const k of ['ko', 'en', 'ar']) {
			if (k in o && typeof o[k] === 'string' && (o[k] as string).trim()) {
				return (o[k] as string).trim();
			}
		}
		for (const v of Object.values(o)) {
			if (typeof v === 'string' && v.trim()) return v.trim();
		}
		for (const k of ['value', 'str', 'text', 'content', 'data']) {
			if (k in o && typeof o[k] === 'string' && (o[k] as string).trim()) {
				return (o[k] as string).trim();
			}
		}
	}
	return String(raw);
}

function coerceHandoverFromApi(v: unknown, locale?: string): string {
	return pickHandoverTextForLocale(v, locale);
}

export type ConvertScenarioBasicInfoOptions = {
	/** 라우트의 시나리오 ID(GET /scenario/{id}/info) — DTO에 없을 때 화면용 id로 사용 */
	scenarioId?: number;
	/** `i18n.language` / `useApiLanguage()` — handoverNote가 언어별 객체일 때 */
	locale?: string;
};

/**
 * GET /scenario/{id}/info 응답 → 화면용 Scenario (플로우·맵 상세 없음)
 */
export const convertScenarioBasicInfoToScenario = (
	dto: GetScenarioBasicInfoResponseDto,
	options?: ConvertScenarioBasicInfoOptions
): Scenario => {
	const secs = coerceNonNegativeSeconds(dto.expectedPlayTimeSeconds);
	const durationLabel = formatExpectedPlaySecondsForDisplay(secs);
	const objTasks = (dto.learningObjectives ?? []).map((lo) => lo.title).filter(Boolean);
	const handover = coerceHandoverFromApi(dto.handoverNote as unknown, options?.locale);
	const thumbRaw = coerceUrlLikeString(dto.thumbnailImageUrl);
	const thumb = resolveScenarioMediaUrl(thumbRaw);
	const dtoAny = dto as GetScenarioBasicInfoResponseDto & Record<string, unknown>;
	const resolvedScenarioId =
		options?.scenarioId ??
		pickFirstPositiveInt(dtoAny.scenarioId, dtoAny.scenario_id) ??
		dto.scenarioCategoryId;

	return {
		id: resolvedScenarioId,
		title: dto.title,
		classId: '',
		category: '',
		platform: 'VR' as any,
		status: 'active',
		learningObjectives: objTasks.length > 0 ? objTasks : [],
		duration: durationLabel,
		handover,
		version: '',
		createdDate: '',
		updatedDate: '',
		views: 0,
		userCount: 0,
		simulationCount: 0,
		ContributedBy: '',
		supportedDevices: [],
		videoUrl: '',
		thumbnail: thumb,
		keyTasks: objTasks,
		simulationExamples: [],
		isPublic: true,
		averageDuration: durationLabel,
		averageScore: 0,
		patient: '정보 없음',
		map: typeof dto.map === 'string' ? dto.map : '—',
		contentCode: dto.key,
	};
};

/**
 * ScenarioAdminDto를 Scenario 타입으로 변환
 * (백엔드 Admin API 구현 후 사용)
 */
export const convertScenarioAdminDtoToScenario = (dto: ScenarioAdminDto): Scenario => {
	const durationLabel = formatExpectedPlaySecondsForDisplay(
		Math.max(0, Math.floor(Number(dto.expectedPlayTimeSeconds) || 0))
	);
	// deviceNames 우선, 없으면 deviceTypeList 폴백
	const deviceNames: string[] =
		dto.deviceNames && dto.deviceNames.length > 0
			? dto.deviceNames
			: (dto.deviceTypeList?.map((d) => d.title) ?? []);
	const platformStr = deviceNames.length > 0 ? deviceNames.join(', ') : '-';

	return {
		id: dto.scenarioId,
		title: dto.title,
		classId: '', // classId는 DTO에 없으므로 빈 문자열
		category: (dto.scenarioCategoryList ?? []).map((c) => c.title).join(', '),
		platform: platformStr as any,
		status: dto.status || 'active',
		createdDate: formatScenarioListDate(dto.createdAt),
		updatedDate: formatScenarioListDate(dto.createdAt),
		version: dto.version,
		averageDuration: durationLabel,
		views: dto.views || 0,
		userCount: dto.userCount || 0,
		simulationCount: dto.simulationCount || 0,
		ContributedBy: dto.contributedBy || '',
		learningObjectives: [],
		duration: durationLabel,
		supportedDevices: [],
		videoUrl: '',
		thumbnail: dto.thumbnailImageUrl,
		handover: '',
		keyTasks: [],
		simulationExamples: [],
		isPublic: dto.status === 'active',
		averageScore: 0,
		...(dto.modeValueTitle ? { modeValueTitle: dto.modeValueTitle } : {}),
	};
};

/**
 * 시나리오 상태 배지 정보 반환
 */
export const getScenarioStatusBadge = (status: string): {
	label: string;
	variant: 'default' | 'secondary' | 'destructive';
} => {
	const badges: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
		active: { label: '발행완료', variant: 'default' },
		inactive: { label: '비공개', variant: 'secondary' },
		draft: { label: '작성중', variant: 'secondary' },
	};
	return badges[status] || badges.active;
};
